import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../lib/ui";
import { Loader2, AlertOctagon, CheckCircle2 } from "lucide-react";
import { ZoneMap } from "../components/ZoneMap";

const NAT = ["admin", "program_manager", "si_analyst"];
const LEVELS = { agent: "Agent", zone: "Zone de santé", province: "Province", national: "National", quality: "Qualité et supervision" };
const pct = (v) => (v == null ? "—" : `${v} %`);

function Kpi({ k }) {
  const { t } = useLang();
  const bad = k.warn && k.value !== 0 && k.value !== "—" && k.value !== "Faite";
  return (
    <div className={`rounded-lg border bg-white p-3 ${bad ? "border-red-200" : "border-slate-200"}`} data-testid={`ov-kpi-${k.k}`}>
      <p className="text-[11px] uppercase tracking-wider text-slate-500">{t(k.label)}</p>
      <p className={`text-2xl font-semibold ${bad ? "text-red-600" : k.ok ? "text-emerald-700" : "text-slate-900"}`}>{typeof k.value === "string" ? t(k.value) : k.value}</p>
    </div>
  );
}

export default function Overview() {
  const { t } = useLang();
  const { user } = useAuth();
  const nat = NAT.includes(user?.role);
  const [level, setLevel] = useState(nat ? "national" : "");
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);

  useEffect(() => {
    setD(null);
    api.get("/overview", { params: { ...(level ? { level } : {}), days } }).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  }, [level, days]);

  return (
    <div data-testid="overview-page">
      <PageHeader eyebrow={t("En un coup d'œil")} title={t("Vue d'ensemble")} subtitle={t("Les chiffres essentiels de votre niveau, calculés à partir des saisies validées.")} testid="overview-header">
        {nat && (
          <select value={level} onChange={(e) => setLevel(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="ov-level">
            {Object.entries(LEVELS).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
          </select>
        )}
        {d?.level !== "agent" && (
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="ov-days">
            <option value={7}>{t("7 derniers jours")}</option><option value={30}>{t("30 derniers jours")}</option><option value={90}>{t("90 derniers jours")}</option>
          </select>
        )}
      </PageHeader>

      {!d ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#F05A28]" /></div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="ov-kpis">{d.kpis.map((k) => <Kpi key={k.k} k={k} />)}</div>

          {d.level === "agent" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="ov-history">
                <h2 className="text-sm font-semibold text-slate-900 mb-2">{t("Mes 14 dernières saisies")}</h2>
                <ul className="text-sm divide-y">{d.history.map((h) => <li key={h.date} className="py-1.5 flex justify-between"><span>{h.date} <span className="text-[10px] uppercase text-slate-400">{h.source}</span></span>{h.flags ? <span className="text-xs text-amber-700">{h.flags} {t("point(s) qualité")}</span> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}</li>)}</ul>
                {d.history.length === 0 && <p className="text-xs text-slate-400 italic">{t("Aucune saisie.")}</p>}
                <Link to="/entry" className="inline-block mt-3 text-sm font-semibold text-[#F05A28]" data-testid="ov-go-entry">{t("Ouvrir ma journée")} →</Link>
              </section>
              <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="ov-reports">
                <h2 className="text-sm font-semibold text-slate-900 mb-2">{t("Mes rapports récents")}</h2>
                <ul className="text-sm divide-y">{d.reports.map((r) => <li key={r.id} className="py-1.5 flex justify-between"><Link to={`/generated/${r.id}`} className="hover:underline">{r.period_label}</Link><span className="text-xs text-slate-500">{t(r.status)}</span></li>)}</ul>
              </section>
            </div>
          )}

          {d.level !== "agent" && (
            <div className="space-y-4">
              <section data-testid="ov-map">
                <h2 className="text-sm font-semibold text-slate-900 mb-2">{t("Carte des zones de santé · complétude de saisie")}</h2>
                <ZoneMap province={d.level === "national" ? "" : user?.province} highlight={d.level === "zone" ? user?.health_zone : ""} height={320} testid="ov-zone-map" />
              </section>
              <section className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto" data-testid="ov-zones">
                <h2 className="text-sm font-semibold text-slate-900 mb-2">{t(d.level === "national" ? "Comparaison des zones et provinces" : "Comparaison des zones de santé")}</h2>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b"><th className="py-2 pr-3">{t("Zone")}</th><th className="py-2 pr-3">{t("Saisies")}</th><th className="py-2 pr-3">{t("Complétude")}</th><th className="py-2 pr-3">{t("Alertes")}</th><th className="py-2 pr-3">{t("Vérification")}</th><th className="py-2 pr-3">{t("Suivi contacts")}</th><th className="py-2 pr-3">{t("Cas")}</th><th className="py-2 pr-3">{t("Qualité")}</th><th className="py-2">{t("Dernière saisie")}</th></tr></thead>
                  <tbody>{d.rows.map((r) => (
                    <tr key={r.zone} className="border-b border-slate-100" data-testid={`ov-zone-${r.zone}`}>
                      <td className="py-2 pr-3 font-medium">{r.zone}</td><td className="py-2 pr-3">{r.entries}</td><td className="py-2 pr-3">{pct(r.completeness)}</td><td className="py-2 pr-3">{r.alerts}</td>
                      <td className="py-2 pr-3">{pct(r.verification)}</td><td className={`py-2 pr-3 ${r.followup != null && r.followup < 90 ? "text-red-600 font-semibold" : ""}`}>{pct(r.followup)}</td><td className="py-2 pr-3">{r.confirmed}</td>
                      <td className="py-2 pr-3">{r.flags ? <Link to="/qualite" className="text-amber-700 font-semibold">{r.flags}</Link> : <span className="text-emerald-700">0</span>}</td><td className="py-2 text-slate-500">{r.last}</td>
                    </tr>))}</tbody>
                </table>
                {d.rows.length === 0 && <p className="text-xs text-slate-400 italic">{t("Aucune saisie sur la période.")}</p>}
              </section>
              {d.silent_zones?.length > 0 && <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm" data-testid="ov-silent"><b>{t("Zones sans aucune saisie")} :</b> {d.silent_zones.join(", ")}</section>}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="ov-overdue">
                  <h2 className="text-sm font-semibold text-slate-900 mb-2 inline-flex items-center gap-1.5"><AlertOctagon className="h-4 w-4 text-red-600" />{t("Actions en retard")}</h2>
                  <ul className="text-sm divide-y">{d.overdue.map((a) => <li key={a.id} className="py-1.5">{a.action} <span className="text-xs text-slate-500">· {a.owner} · {a.due_date} · {a.health_zone || a.province}</span></li>)}</ul>
                  {d.overdue.length === 0 && <p className="text-xs text-slate-400 italic">{t("Aucune action en retard.")}</p>}
                  <Link to="/actions" className="inline-block mt-3 text-sm font-semibold text-[#F05A28]">{t("Suivi des actions")} →</Link>
                </section>
                {d.challenges && (
                  <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="ov-challenges">
                    <h2 className="text-sm font-semibold text-slate-900 mb-2">{t("Difficultés signalées cette semaine")}</h2>
                    <ul className="text-sm divide-y">{d.challenges.map((c, i) => <li key={i} className="py-1.5">{c.issue} <span className="text-xs text-slate-500">· {c.date} · {c.author_name}</span></li>)}</ul>
                    {d.challenges.length === 0 && <p className="text-xs text-slate-400 italic">{t("Aucune difficulté signalée.")}</p>}
                    <Link to="/terrain" className="inline-block mt-3 text-sm font-semibold text-[#F05A28]">{t("Terrain")} →</Link>
                  </section>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
